import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';

const RetailDiningService = () => {
  const navigate = useNavigate();
  const [selectedFeature, setSelectedFeature] = useState('overview');

  const features = [
    {
      id: 'inventory',
      title: 'Smart Inventory Management',
      icon: 'inventory_2',
      description: 'AI-powered inventory optimization with predictive restocking and waste reduction.',
      benefits: ['Reduce waste by 35%', 'Automate reordering', 'Real-time tracking', 'Cost optimization'],
      metrics: { accuracy: '98%', savings: '$50K/year', efficiency: '40% faster' }
    },
    {
      id: 'pos',
      title: 'Intelligent POS System',
      icon: 'point_of_sale',
      description: 'Next-generation point-of-sale with AI recommendations and customer insights.',
      benefits: ['Increase sales by 25%', 'Personalized recommendations', 'Fraud detection', 'Analytics dashboard'],
      metrics: { uptime: '99.9%', transactions: '10K+/day', satisfaction: '4.8/5' }
    },
    {
      id: 'customer',
      title: 'Customer Experience AI',
      icon: 'sentiment_satisfied',
      description: 'Enhance customer satisfaction with AI-driven personalization and service optimization.',
      benefits: ['Personalized experiences', 'Sentiment analysis', 'Loyalty programs', 'Feedback automation'],
      metrics: { retention: '+30%', satisfaction: '95%', engagement: '+45%' }
    },
    {
      id: 'operations',
      title: 'Operations Automation',
      icon: 'automation',
      description: 'Streamline restaurant operations with automated scheduling, ordering, and workflow management.',
      benefits: ['Staff optimization', 'Automated scheduling', 'Supply chain integration', 'Performance analytics'],
      metrics: { efficiency: '+50%', costs: '-25%', accuracy: '99%' }
    }
  ];

  const useCases = [
    {
      title: 'Quick Service Restaurants',
      description: 'Optimize high-volume operations with automated ordering, inventory management, and customer flow analysis.',
      icon: 'fastfood',
      industries: ['Fast Food', 'Coffee Shops', 'Food Trucks']
    },
    {
      title: 'Fine Dining Establishments',
      description: 'Enhance premium dining experiences with personalized service, wine pairing suggestions, and reservation optimization.',
      icon: 'restaurant_menu',
      industries: ['Fine Dining', 'Wine Bars', 'Specialty Restaurants']
    },
    {
      title: 'Retail Chains',
      description: 'Manage multi-location inventory, customer data, and sales analytics across all store locations.',
      icon: 'store',
      industries: ['Retail Chains', 'Franchises', 'Department Stores']
    },
    {
      title: 'Food Service Operations',
      description: 'Optimize large-scale food service with menu planning, nutritional analysis, and cost management.',
      icon: 'local_dining',
      industries: ['Catering', 'Corporate Dining', 'Institutional Food Service']
    }
  ];

  const integrations = [
    { name: 'Square', icon: 'payment', category: 'Payment Processing' },
    { name: 'Toast', icon: 'restaurant', category: 'POS Systems' },
    { name: 'OpenTable', icon: 'event_seat', category: 'Reservations' },
    { name: 'DoorDash', icon: 'delivery_dining', category: 'Delivery' },
    { name: 'Yelp', icon: 'star', category: 'Reviews' },
    { name: 'QuickBooks', icon: 'account_balance', category: 'Accounting' }
  ];

  return (
    <div className="min-h-screen bg-dark">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-transparent to-orange-500/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="w-16 h-16 bg-amber-500/20 rounded-2xl flex items-center justify-center">
                <span className="material-symbols-outlined text-amber-500 text-3xl">storefront</span>
              </div>
              <div className="text-left">
                <h1 className="text-4xl md:text-6xl font-bold text-white">
                  Retail & <span className="text-amber-500">Dining</span>
                </h1>
                <p className="text-gray-400 text-lg mt-2">AI-Powered Restaurant & Retail Operations</p>
              </div>
            </div>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Transform your restaurant or retail business with intelligent automation that optimizes operations, 
              enhances customer experiences, and drives profitability through data-driven insights.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all transform hover:scale-105">
                Start Free Trial
              </button>
              <button className="border border-gray-600 hover:border-amber-500 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all">
                Schedule Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Navigation */}
      <section className="py-12 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {features.map((feature) => (
              <button
                key={feature.id}
                onClick={() => setSelectedFeature(feature.id)}
                className={`flex items-center gap-3 px-6 py-3 rounded-lg transition-all ${
                  selectedFeature === feature.id
                    ? 'bg-amber-500 text-white shadow-lg'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                <span className="material-symbols-outlined">{feature.icon}</span>
                <span className="font-medium">{feature.title}</span>
              </button>
            ))}
          </div>

          {/* Feature Details */}
          {features.map((feature) => (
            selectedFeature === feature.id && (
              <div key={feature.id} className="bg-gray-800 rounded-2xl p-8 border border-gray-700">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center">
                        <span className="material-symbols-outlined text-amber-500 text-2xl">{feature.icon}</span>
                      </div>
                      <h3 className="text-2xl font-bold text-white">{feature.title}</h3>
                    </div>
                    <p className="text-gray-300 text-lg mb-6">{feature.description}</p>
                    <div className="space-y-3">
                      <h4 className="text-white font-semibold">Key Benefits:</h4>
                      <ul className="space-y-2">
                        {feature.benefits.map((benefit, index) => (
                          <li key={index} className="flex items-center gap-3 text-gray-300">
                            <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="bg-gray-900 rounded-xl p-6">
                    <h4 className="text-white font-semibold mb-4">Performance Metrics</h4>
                    <div className="grid grid-cols-1 gap-4">
                      {Object.entries(feature.metrics).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-center p-3 bg-gray-800 rounded-lg">
                          <span className="text-gray-400 capitalize">{key}</span>
                          <span className="text-amber-500 font-bold">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )
          ))}
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Industry Applications</h2>
            <p className="text-gray-300 text-lg">Tailored solutions for different business types</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-amber-500/50 transition-all">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center">
                    <span className="material-symbols-outlined text-amber-500 text-2xl">{useCase.icon}</span>
                  </div>
                  <h3 className="text-xl font-bold text-white">{useCase.title}</h3>
                </div>
                <p className="text-gray-300 mb-4">{useCase.description}</p>
                <div className="flex flex-wrap gap-2">
                  {useCase.industries.map((industry, idx) => (
                    <span key={idx} className="bg-amber-500/10 text-amber-400 px-3 py-1 rounded-full text-sm">
                      {industry}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section className="py-16 bg-gray-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Seamless Integrations</h2>
            <p className="text-gray-300 text-lg">Connect with your existing tools and platforms</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {integrations.map((integration, index) => (
              <div key={index} className="bg-gray-800 rounded-lg p-4 text-center hover:bg-gray-700 transition-all">
                <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <span className="material-symbols-outlined text-amber-500">{integration.icon}</span>
                </div>
                <h4 className="text-white font-medium text-sm mb-1">{integration.name}</h4>
                <p className="text-gray-400 text-xs">{integration.category}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-amber-500/10 to-orange-500/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Transform Your Business?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Join thousands of restaurants and retailers already using OrbitMind to optimize their operations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/signup')}
              className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all transform hover:scale-105"
            >
              Get Started Today
            </button>
            <button 
              onClick={() => navigate('/contact')}
              className="border border-amber-500 hover:bg-amber-500 text-amber-500 hover:text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all"
            >
              Contact Sales
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RetailDiningService;